import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import TypeAhead, { TypeAheadOption } from './TypeAhead';
import { searchLocations } from '../services/locations.service';
import type { LocationLite } from '../types/locations';

/** Display name rule shared by every location picker: alias wins over lname. */
export function locationDisplayName(loc: LocationLite): string {
  return loc.cyalias?.trim() ? loc.cyalias : loc.lname;
}

/** Two-line address block written to {orig,dest}CityState on a leg pick. */
export function locationCityState(loc: LocationLite): string {
  return `${loc.ladd}\n${loc.lcity}, ${loc.lstate}`;
}

/**
 * Option-label rule shared by every location picker — the order form's legs and the
 * table-maintenance drawers alike: "ALIAS - NAME" when the location has an alias, else
 * the name. Since the pickers' TypeAhead filters on the label, this is what lets a user
 * find a location by either. A blank alias, or one that merely repeats the name (V1
 * seeded many that way), is dropped rather than shown as "X - X". Mirrors
 * cms_lookups._location_label on the backend, which feeds the generic tablemaint selects.
 */
export function locationOptionLabel(name: string, alias?: string | null): string {
  const n = (name ?? '').trim();
  const a = (alias ?? '').trim();
  if (!a || a.toLowerCase() === n.toLowerCase()) return n;
  return `${a} - ${n}`;
}

function toOption(loc: LocationLite, withRecnoSuffix?: boolean): TypeAheadOption {
  return {
    value: String(loc.recno),
    label: locationOptionLabel(loc.lname, loc.cyalias),
    ...(withRecnoSuffix ? { labelSuffix: `#${loc.recno}` } : {}),
    description: `${loc.ladd}, ${loc.lcity}, ${loc.lstate}`,
    data: loc,
  };
}

const MIN_CHARS = 2;
// Matches LookupSearchField's debounce so all async lookups feel the same.
const DEBOUNCE_MS = 300;

interface LocationTypeAheadProps {
  /** Selected location recno as a string, '' when unset. */
  value: string;
  /** Stored display name for `value` — shown in the input without a lookup
   *  round-trip (search results only cover the current term). */
  valueLabel?: string;
  /** Fires with the picked location, or null when the field is cleared. */
  onPick: (loc: LocationLite | null) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  compact?: boolean;
  /** Green "#recno" adornment on each option (linked-record style — see the
   *  recno-rollout convention). Off for the legs grid. */
  withRecnoSuffix?: boolean;
  /** When provided, pins an "Add New Location" action to the bottom of the
   *  result list (and to the no-results / type-more states) — same affordance
   *  as the Customer / Carrier lookups. Receives the typed search term so the
   *  caller can seed the new location's name. */
  onAddNew?: (term: string) => void;
}

/**
 * Server-searched location picker (`GET /admin/locations?q=`), replacing the
 * preloaded full-list TypeAheads. The old pattern fetched every location once
 * on mount, so a location created mid-session (e.g. in V1) never appeared
 * until a full reload — ops hit "No results found" on locations that existed.
 * Searching live also drops the ~0.5MB list from the Pro drawer's open burst.
 *
 * Matches name / alias / city / address, or the location # when the term is
 * all digits. Debounced 300ms, min 2 chars, in-flight requests aborted on
 * retype.
 */
const LocationTypeAhead: React.FC<LocationTypeAheadProps> = ({
  value,
  valueLabel,
  onPick,
  disabled,
  placeholder = 'Search location…',
  ariaLabel,
  className,
  compact,
  withRecnoSuffix,
  onAddNew,
}) => {
  const [results, setResults] = useState<TypeAheadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [term, setTerm] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Guards against out-of-order resolutions (slow response for an old term
  // landing after a fast one for the current term).
  const seqRef = useRef(0);

  const handleSearch = useCallback((raw: string) => {
    setTerm(raw);
    if (timerRef.current) clearTimeout(timerRef.current);
    const needle = raw.trim();
    if (needle.length < MIN_CHARS) {
      abortRef.current?.abort();
      seqRef.current += 1;
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timerRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const seq = ++seqRef.current;
      searchLocations(needle, { signal: controller.signal })
        .then((locs) => {
          if (seq !== seqRef.current) return;
          setResults(locs.map((loc) => toOption(loc, withRecnoSuffix)));
          setSearching(false);
        })
        .catch((error: { name?: string; code?: string }) => {
          if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
          if (seq !== seqRef.current) return;
          console.error('Error searching locations:', error);
          setResults([]);
          setSearching(false);
        });
    }, DEBOUNCE_MS);
  }, [withRecnoSuffix]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    },
    [],
  );

  // Drop the current search entirely — pending debounce, in-flight request,
  // results, term. Used when the search is "consumed" (a pick) or abandoned
  // (dropdown closed without picking).
  const resetSearch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();
    seqRef.current += 1;
    setResults([]);
    setTerm('');
    setSearching(false);
  }, []);

  // The selected value's option must exist in `options` for TypeAhead to
  // resolve its input label — synthesize it from the stored name when the
  // current search results don't include it. `hidden`: it exists ONLY for
  // that resolution — the dropdown never lists the current pick as a row
  // (it stole the keyboard highlight during a search, so Enter re-selected
  // the previous location instead of the top hit).
  const options = useMemo<TypeAheadOption[]>(() => {
    if (!value || results.some((o) => o.value === value)) return results;
    const selected: TypeAheadOption = {
      value,
      label: valueLabel?.trim() || `#${value}`,
      ...(withRecnoSuffix ? { labelSuffix: `#${value}` } : {}),
      hidden: true,
    };
    return [selected, ...results];
  }, [results, value, valueLabel, withRecnoSuffix]);

  const handleChange = (next: string) => {
    // Picks arrive via onSelect (which carries the full row); onChange only
    // matters for the clear (✕) path.
    if (!next) onPick(null);
  };

  return (
    <TypeAhead
      preserveValueOnFocus
      options={options}
      value={value}
      onChange={handleChange}
      onSelect={(option) => {
        const loc = option.data as LocationLite | undefined;
        if (!loc) return;
        onPick(loc);
        // Consume the search: reopening later shows the pick (via the
        // synthetic option), not the leftover result list for the old term.
        resetSearch();
      }}
      // Closed without picking (click-away / Escape): drop the abandoned
      // search so the synthetic option returns and the stored label shows
      // again — and the next open starts clean instead of on old results.
      onClose={resetSearch}
      onSearch={handleSearch}
      onCreateNew={onAddNew}
      createNewLabel={(t) => `Add New Location${t.trim() ? `: "${t.trim()}"` : ''}`}
      // Results are already server-filtered — the client filter would hide a
      // recno search's match (its digits aren't in the label/description).
      serverFiltered
      searching={searching}
      emptyMessage={
        term.trim().length < MIN_CHARS ? 'Type at least 2 characters to search' : undefined
      }
      placeholder={placeholder}
      disabled={disabled}
      showSearch
      showDescriptions
      compact={compact}
      className={className}
      ariaLabel={ariaLabel}
    />
  );
};

export default LocationTypeAhead;
